import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ShoppingCart,
  Favorite,
  AccountCircle,
  Menu as MenuIcon,
  Phone,
  Email,
  Agriculture,
  Store,
  Event,
  Hotel,
  Info,
  ContactMail,
  Login,
  PersonAdd,
  PhotoLibrary,
  HistoryEdu,
} from '@mui/icons-material';
import vidhiLogo from '../../assets/vidhi.png';
import { surface, brand, ink, line } from '../../theme/tokens';
import { logoutUser } from '../../store/slices/authSlice';
import { selectCartQuantity } from '../../store/slices/cartSlice';
import { selectFavoritesCount } from '../../store/slices/favoritesSlice';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const cartQuantity = useSelector(selectCartQuantity);
  const favoritesCount = useSelector(selectFavoritesCount);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigationItems = [
    { label: 'Home', path: '/', icon: Agriculture },
    // The stay is the highest-value thing on the site and was reachable only by
    // scrolling the home page -- it was in neither nav.
    { label: 'Farm Stay', path: '/farm-stay', icon: Hotel },
    { label: 'Our Story', path: '/about', icon: HistoryEdu },
    { label: 'Products', path: '/products', icon: Store },
    { label: 'Blog', path: '/blog', icon: Info },
    { label: 'Gallery', path: '/gallery', icon: PhotoLibrary },
    { label: 'Contact', path: '/contact', icon: ContactMail },
  ];

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    handleMenuClose();
    navigate('/');
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const MobileMenu = () => (
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={handleMobileMenuToggle}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          backgroundColor: surface.dark,
          // The drawer is the dark panel: force the on-dark ink across it so no
          // variant or `color=` prop can drop light-surface text onto brown.
          color: ink.onDark,
          '& .MuiListItemText-primary': {
            color: `${ink.onDark} !important`,
          },
          '& .MuiTypography-root': {
            color: `${ink.onDark} !important`,
          },
          '& .MuiListItemIcon-root': {
            color: `${ink.onDark} !important`,
          },
          '& .MuiSvgIcon-root': {
            color: `${ink.onDark} !important`,
          },
          '& *': {
            color: `${ink.onDark} !important`,
          },
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h6"
          sx={{
            color: ink.onDark,
            mb: 2,
            pb: 1.5,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: '0.875rem',
            borderBottom: `2px solid ${brand.mustard}`,
            display: 'inline-block',
          }}
        >
          Vidhi Farm
        </Typography>
        <List>
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <ListItem
                key={item.path}
                button
                component={Link}
                to={item.path}
                onClick={handleMobileMenuToggle}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  borderLeft: '3px solid',
                  borderLeftColor: isActiveRoute(item.path) ? brand.mustard : 'transparent',
                  backgroundColor: isActiveRoute(item.path)
                    ? 'rgba(242, 241, 236, 0.10)'
                    : 'transparent',
                  '& .MuiListItemText-primary': {
                    fontWeight: isActiveRoute(item.path) ? 600 : 400,
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(242, 241, 236, 0.10)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: ink.onDark, minWidth: 40 }}>
                  <IconComponent />
                </ListItemIcon>
                <ListItemText 
                  primary={item.label} 
                  sx={{ 
                    color: `${ink.onDark} !important`,
                    '& .MuiListItemText-primary': {
                      color: `${ink.onDark} !important`
                    }
                  }}
                />
              </ListItem>
            );
          })}
        </List>
        
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(242, 241, 236, 0.20)' }}>
          {/* Cart and Favorites */}
          <ListItem
            button
            component={Link}
            to="/cart"
            onClick={handleMobileMenuToggle}
          >
            <ListItemIcon sx={{ color: ink.onDark }}>
              <Badge badgeContent={cartQuantity} color="error">
                <ShoppingCart />
              </Badge>
            </ListItemIcon>
            <ListItemText 
              primary="Shopping Cart" 
              sx={{ 
                color: `${ink.onDark} !important`,
                '& .MuiListItemText-primary': {
                  color: `${ink.onDark} !important`
                }
              }}
            />
          </ListItem>
          
          {isAuthenticated && (
            <ListItem
              button
              component={Link}
              to="/favorites"
              onClick={handleMobileMenuToggle}
            >
              <ListItemIcon sx={{ color: ink.onDark }}>
                <Badge badgeContent={favoritesCount} color="error">
                  <Favorite />
                </Badge>
              </ListItemIcon>
              <ListItemText 
                primary="My Favorites" 
                sx={{ 
                  color: `${ink.onDark} !important`,
                  '& .MuiListItemText-primary': {
                    color: `${ink.onDark} !important`
                  }
                }}
              />
            </ListItem>
          )}
          
          {isAuthenticated ? (
            <>
              <ListItem
                button
                component={Link}
                to={user?.role === 'admin' ? '/admin' : '/customer/orders'}
                onClick={handleMobileMenuToggle}
              >
                <ListItemIcon sx={{ color: ink.onDark }}>
                  <AccountCircle />
                </ListItemIcon>
                <ListItemText 
                  primary={user?.role === 'admin' ? 'Admin Dashboard' : 'My Orders'} 
                  sx={{ 
                    color: `${ink.onDark} !important`,
                    '& .MuiListItemText-primary': {
                      color: `${ink.onDark} !important`
                    }
                  }}
                />
              </ListItem>
              <ListItem button onClick={() => { handleLogout(); handleMobileMenuToggle(); }}>
                <ListItemIcon sx={{ color: ink.onDark }}>
                  <Login />
                </ListItemIcon>
                <ListItemText 
                  primary="Logout" 
                  sx={{ 
                    color: `${ink.onDark} !important`,
                    '& .MuiListItemText-primary': {
                      color: `${ink.onDark} !important`
                    }
                  }}
                />
              </ListItem>
            </>
          ) : (
            <>
              <ListItem
                button
                component={Link}
                to="/login"
                onClick={handleMobileMenuToggle}
              >
                <ListItemIcon sx={{ color: ink.onDark }}>
                  <Login />
                </ListItemIcon>
                <ListItemText 
                  primary="Login" 
                  sx={{ 
                    color: `${ink.onDark} !important`,
                    '& .MuiListItemText-primary': {
                      color: `${ink.onDark} !important`
                    }
                  }}
                />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/register"
                onClick={handleMobileMenuToggle}
              >
                <ListItemIcon sx={{ color: ink.onDark }}>
                  <PersonAdd />
                </ListItemIcon>
                <ListItemText 
                  primary="Register" 
                  sx={{ 
                    color: `${ink.onDark} !important`,
                    '& .MuiListItemText-primary': {
                      color: `${ink.onDark} !important`
                    }
                  }}
                />
              </ListItem>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: surface.card,
          borderBottom: `1px solid ${line.default}`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1, gap: 2 }}>
            {/* Logo Section */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isMobile && (
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleMobileMenuToggle}
                  sx={{ 
                    mr: 1,
                    color: brand.brown,
                    '&:hover': {
                      backgroundColor: line.subtle
                    }
                  }}
                >
                  <MenuIcon sx={{ color: brand.brown }} />
                </IconButton>
              )}
              <Box
                component={Link}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <Box
                  component="img"
                  src={vidhiLogo}
                  alt="Vidhi Farm Logo"
                  sx={{
                    height: 40,
                    width: 'auto',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    // Fallback to icon if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <Box sx={{ display: 'none', alignItems: 'center' }}>
                  <Agriculture sx={{ fontSize: 32, color: brand.brown }} />
                </Box>
              </Box>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {navigationItems.map((item) => (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    sx={{
                      // active reads as the darkest ink plus a mustard rule;
                      // inactive steps back to ink.secondary (6.54:1 on the bar)
                      color: isActiveRoute(item.path) ? ink.primary : ink.secondary,
                      fontWeight: isActiveRoute(item.path) ? 600 : 500,
                      letterSpacing: '0.01em',
                      borderRadius: 0,
                      px: 2,
                      py: 1,
                      borderBottom: '2px solid',
                      borderBottomColor: isActiveRoute(item.path) ? brand.mustard : 'transparent',
                      transition: 'color 0.2s ease, border-color 0.2s ease',
                      '&:hover': {
                        color: ink.primary,
                        backgroundColor: line.subtle,
                        borderBottomColor: brand.mustard,
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Right Side Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

              {/* Favorites - Only show if authenticated */}
              {isAuthenticated && (
                <IconButton
                  component={Link}
                  to="/favorites"
                  color="inherit"
                  sx={{ 
                    color: brand.brown,
                    '&:hover': { backgroundColor: line.subtle },
                  }}
                >
                  <Badge badgeContent={favoritesCount} color="error">
                    <Favorite />
                  </Badge>
                </IconButton>
              )}

              {/* Shopping Cart */}
              <IconButton
                component={Link}
                to="/cart"
                color="inherit"
                sx={{ 
                  color: brand.brown,
                  '&:hover': { backgroundColor: line.subtle },
                }}
              >
                <Badge badgeContent={cartQuantity} color="error">
                  <ShoppingCart />
                </Badge>
              </IconButton>

              {/* User Account */}
              {isAuthenticated ? (
                <>
                  <IconButton
                    onClick={handleMenuOpen}
                    color="inherit"
                    aria-label="Your account"
                    aria-haspopup="true"
                    sx={{ 
                      color: brand.brown,
                      '&:hover': { backgroundColor: line.subtle },
                    }}
                  >
                    <AccountCircle />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem onClick={handleMenuClose}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ink.primary }}>
                        Hello, {user?.name || user?.email}
                      </Typography>
                    </MenuItem>
                    {user?.role === 'admin' ? (
                      <>
                        <MenuItem component={Link} to="/admin" onClick={handleMenuClose}>
                          Admin Dashboard
                        </MenuItem>
                        <MenuItem component={Link} to="/admin/users" onClick={handleMenuClose}>
                          User Management
                        </MenuItem>
                        <MenuItem component={Link} to="/admin/products" onClick={handleMenuClose}>
                          Product Management
                        </MenuItem>
                        <MenuItem component={Link} to="/admin/bookings" onClick={handleMenuClose}>
                          Booking Management
                        </MenuItem>
                        <MenuItem component={Link} to="/admin/reports" onClick={handleMenuClose}>
                          Reports
                        </MenuItem>
                        <MenuItem component={Link} to="/admin/settings" onClick={handleMenuClose}>
                          Settings
                        </MenuItem>
                      </>
                    ) : (
                      <>
                        <MenuItem component={Link} to="/customer/orders" onClick={handleMenuClose}>
                          My Orders
                        </MenuItem>
                      </>
                    )}
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                  </Menu>
                </>
              ) : (
                !isMobile && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      component={Link}
                      to="/login"
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 80 }}
                    >
                      Login
                    </Button>
                    <Button
                      component={Link}
                      to="/register"
                      variant="contained"
                      size="small"
                      sx={{ minWidth: 80 }}
                    >
                      Register
                    </Button>
                  </Box>
                )
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      
      <MobileMenu />
    </>
  );
};

export default Header;